import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { HelpIcon } from './HelpIcon';
import { InlineTip } from './InlineTip';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare,
  User,
  Filter,
  Search,
  MoreHorizontal,
  Plus,
  Calendar,
  Tag,
  Eye,
  Edit,
  Trash2,
  UserPlus,
  Send,
  BarChart3,
  TrendingUp,
  Award,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Types
interface Feedback {
  id: string;
  title: string;
  description: string;
  rating?: number;
  feedback_text?: string;
  status: 'NEW' | 'ASSIGNED' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  source: 'RATING_GATE' | 'EMAIL' | 'PHONE' | 'WEB_FORM' | 'API' | 'MANUAL';
  assigned_to_id?: string;
  assigned_to?: User;
  created_at: string;
  updated_at: string;
  sla_target_hours: number;
  sla_status: 'ON_TIME' | 'WARNING' | 'BREACHED';
  sla_breached_at?: string;
  tags: string[];
  keywords: string[];
  contact?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    company?: string;
  };
  campaign?: {
    id: string;
    name: string;
  };
  comments?: FeedbackComment[];
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface FeedbackComment {
  id: string;
  comment: string;
  is_internal: boolean;
  created_at: string;
  user: User;
  status_change?: string;
  severity_change?: string;
}

interface FeedbackStats {
  total_new: number;
  total_assigned: number;
  total_in_progress: number;
  total_resolved: number;
  total_overdue: number;
  overdue_by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface DateRangeStats {
  total_new: number;
  total_assigned: number;
  total_in_progress: number;
  total_resolved: number;
  total_overdue: number;
  total_feedback: number;
}

interface FeedbackAnalytics {
  total_feedback: number;
  feedback_by_status: Record<string, number>;
  feedback_by_severity: Record<string, number>;
  feedback_by_source: Record<string, number>;
  feedback_by_sla_status: Record<string, number>;
  avg_resolution_time_hours: number | null;
  sla_breach_rate: number;
  feedback_over_time: Array<{
    date: string;
    count: number;
    new: number;
    in_progress: number;
    resolved: number;
    critical: number;
    high: number;
    sla_breached: number;
  }>;
  top_keywords: Array<{
    keyword: string;
    count: number;
  }>;
  assigned_to_performance: Array<{
    user_id: string;
    user_name: string;
    total_assigned: number;
    resolved: number;
    in_progress: number;
    new: number;
    avg_resolution_time_hours: number | null;
    resolution_rate: number;
    sla_breach_count: number;
    sla_breach_rate: number;
  }>;
}

// API functions
const fetchFeedback = async (params: any) => {
  const response = await api.feedback.list(params);
  const data = response.data;
  // Ensure we always return an array
  const feedbackList = Array.isArray(data) ? data : [];
  // Check if there might be more data (if we got a full page, there might be more)
  const hasMore = feedbackList.length === params.limit;
  return { items: feedbackList, hasMore };
};

const fetchFeedbackStats = async () => {
  const response = await api.feedback.getStatsSummary();
  return response.data;
};

const fetchDateRangeStats = async (days: number) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  
  const response = await api.feedback.getAnalytics({
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
  });
  
  const data = response.data;
  // Extract stats from analytics response
  const byStatus = data.feedback_by_status || {};
  const bySlaStatus = data.feedback_by_sla_status || {};
  
  return {
    total_new: byStatus.NEW || 0,
    total_assigned: byStatus.ASSIGNED || 0,
    total_in_progress: byStatus.IN_PROGRESS || 0,
    total_resolved: byStatus.RESOLVED || 0,
    total_overdue: bySlaStatus.BREACHED || 0,
    total_feedback: data.total_feedback || 0,
  };
};

const fetchFeedbackAnalytics = async (params: any) => {
  const response = await api.feedback.getAnalytics({
    start_date: params.start_date,
    end_date: params.end_date,
  });
  return response.data;
};

const updateFeedback = async (id: string, data: any) => {
  const response = await api.feedback.update(id, data);
  return response.data;
};

const resolveFeedback = async (id: string, resolutionNotes: string) => {
  const response = await api.feedback.resolve(id, { resolution_notes: resolutionNotes });
  return response.data;
};

const addComment = async (feedbackId: string, comment: string, isInternal: boolean = false) => {
  const response = await api.feedback.addComment(feedbackId, {
    comment,
    is_internal: isInternal,
  });
  return response.data;
};

// Helper functions
const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
    case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'NEW': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'ASSIGNED': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'RESOLVED': return 'bg-green-100 text-green-800 border-green-200';
    case 'CLOSED': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getSLAStatusColor = (slaStatus: string) => {
  switch (slaStatus) {
    case 'BREACHED': return 'bg-red-100 text-red-800 border-red-200';
    case 'WARNING': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'ON_TIME': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getSLAStatusIcon = (slaStatus: string) => {
  switch (slaStatus) {
    case 'BREACHED': return <XCircle className="h-4 w-4 text-red-600" />;
    case 'WARNING': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
    case 'ON_TIME': return <CheckCircle className="h-4 w-4 text-green-600" />;
    default: return <Clock className="h-4 w-4 text-gray-600" />;
  }
};

const formatSLAStatus = (slaStatus: string) => {
  switch (slaStatus) {
    case 'BREACHED': return 'Breached';
    case 'WARNING': return 'Warning';
    case 'ON_TIME': return 'On Time';
    default: return 'Unknown';
  }
};

// Main component
export default function FeedbackInbox() {
  const [modalState, setModalState] = useState<{
    feedback: Feedback | null;
    mode: 'view' | 'edit' | 'assign' | null;
  }>({ feedback: null, mode: null });
  
  // Keep selectedFeedback for backward compatibility with existing code
  const selectedFeedback = modalState.feedback;
  const modalMode = modalState.mode;
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    sla_status: '',
    search: '',
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(25);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  
  // Assignment state
  const [assignToUserId, setAssignToUserId] = useState<string>('');
  const [tenantMembers, setTenantMembers] = useState<User[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  
  // New Feedback Dialog State
  const [isNewFeedbackDialogOpen, setIsNewFeedbackDialogOpen] = useState(false);
  const [newFeedbackForm, setNewFeedbackForm] = useState({
    title: '',
    description: '',
    rating: '',
    feedback_text: '',
    severity: 'MEDIUM',
    source: 'MANUAL',
    sla_target_hours: '24',
  });
  
  // Analytics state
  const [analyticsDateRange, setAnalyticsDateRange] = useState('30'); // days
  const [activeTab, setActiveTab] = useState('inbox');
  
  // Stats date range state
  const [statsDateRange, setStatsDateRange] = useState('30'); // days

  // Ref for scrolling to feedback table
  const feedbackTableRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { tenant } = useAuth();

  // Queries
  const { data: feedbackData, isLoading: isLoadingFeedback } = useQuery<{ items: Feedback[]; hasMore: boolean }>({
    queryKey: ['feedback', filters, currentPage],
    queryFn: () => fetchFeedback({
      ...filters,
      skip: currentPage * pageSize,
      limit: pageSize,
    }),
  });
  
  const feedbackList = feedbackData?.items || [];
  const hasMore = feedbackData?.hasMore || false;

  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: fetchFeedbackStats,
  });

  // Date range stats query
  const { data: dateRangeStats, isLoading: isLoadingDateRangeStats } = useQuery<DateRangeStats>({
    queryKey: ['feedback-stats-daterange', statsDateRange],
    queryFn: () => fetchDateRangeStats(parseInt(statsDateRange)),
    enabled: activeTab === 'inbox', // Only fetch when on inbox tab
  });

  // Analytics query - only fetch when on analytics tab
  const { data: analytics, isLoading: isLoadingAnalytics } = useQuery<FeedbackAnalytics>({
    queryKey: ['feedback-analytics', analyticsDateRange],
    queryFn: () => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(analyticsDateRange));
      
      return fetchFeedbackAnalytics({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
    },
    enabled: activeTab === 'analytics', // Only fetch when analytics tab is active
  });

  // Mutations
  const updateFeedbackMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateFeedback(id, data),
    onSuccess: (updatedFeedback) => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      // Update the selected feedback with the new data from the API response
      if (selectedFeedback && updatedFeedback.id === selectedFeedback.id) {
        setModalState(prev => ({ ...prev, feedback: updatedFeedback }));
      }
      toast.success('Feedback updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update feedback');
      console.error(error);
    },
  });

  const resolveFeedbackMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => resolveFeedback(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      toast.success('Feedback resolved successfully');
      setModalState({ feedback: null, mode: null });
      setResolutionNotes('');
    },
    onError: (error) => {
      toast.error('Failed to resolve feedback');
      console.error(error);
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: ({ feedbackId, comment, isInternal }: { feedbackId: string; comment: string; isInternal: boolean }) =>
      addComment(feedbackId, comment, isInternal),
    onSuccess: (newComment) => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      // Add the new comment to the selected feedback's comments array
      if (selectedFeedback && newComment.feedback_id === selectedFeedback.id) {
        setModalState(prev => ({
          ...prev,
          feedback: prev.feedback ? {
            ...prev.feedback,
            comments: [...(prev.feedback.comments || []), newComment],
          } : null
        }));
      }
      setNewComment('');
      setIsInternalComment(false);
      toast.success('Comment added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add comment');
      console.error(error);
    },
  });

  const assignFeedbackMutation = useMutation({
    mutationFn: ({ feedbackId, assignedToId }: { feedbackId: string; assignedToId: string }) => {
      // Get API base URL - use same logic as api.ts
      const getApiBaseUrl = (): string => {
        const hostname = window.location.hostname.toLowerCase();
        const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || '';
        
        // If on custom domain, use same domain for API
        if (hostname && 
            !hostname.includes('feedbackgate.com') && 
            !hostname.includes('localhost') && 
            !hostname.includes('127.0.0.1')) {
          const protocol = window.location.protocol;
          if (hostname.startsWith('app.') || hostname.startsWith('www.')) {
            const apiHost = hostname.replace(/^(app|www)\./, 'api.');
            return `${protocol}//${apiHost}`;
          } else {
            return `${protocol}//${hostname}/api`;
          }
        }
        
        return defaultApiUrl || 'http://localhost:8000';
      };
      
      const apiBaseUrl = getApiBaseUrl();
      const accessToken = localStorage.getItem('access_token');
      const tenantId = localStorage.getItem('tenant_id');
      
      return axios.post(`${apiBaseUrl}/api/feedback/assign`, {
        feedback_ids: [feedbackId],
        assigned_to_id: assignedToId,
      }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'X-Tenant-Id': tenantId,
          'Content-Type': 'application/json',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      toast.success('Feedback assigned successfully');
      setModalState({ feedback: null, mode: null });
      setAssignToUserId('');
    },
    onError: (error) => {
      toast.error('Failed to assign feedback');
      console.error(error);
    },
  });

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filters.status, filters.severity, filters.sla_status, filters.search]);

  // Fetch tenant members when assign modal opens
  useEffect(() => {
    if (modalMode === 'assign' && tenant?.id && !isLoadingMembers) {
      setIsLoadingMembers(true);
      api.tenant.getMembers(tenant.id)
        .then((response) => {
          const members = response.data || [];
          setTenantMembers(members.map((m: any) => ({
            id: m.user_id || m.id,
            first_name: m.user?.first_name || m.first_name || '',
            last_name: m.user?.last_name || m.last_name || '',
            email: m.user?.email || m.email || '',
          })));
        })
        .catch((error) => {
          console.error('Error fetching tenant members:', error);
          toast.error('Failed to load team members');
        })
        .finally(() => {
          setIsLoadingMembers(false);
        });
    }
  }, [modalMode, tenant?.id]);

  // Handlers for opening modals
  const handleViewDetails = (feedback: Feedback) => {
    console.log('handleViewDetails called', feedback.id);
    setModalState({ feedback, mode: 'view' });
  };

  const handleEdit = (feedback: Feedback) => {
    console.log('handleEdit called', feedback.id);
    setModalState({ feedback, mode: 'edit' });
  };

  const handleAssign = (feedback: Feedback) => {
    console.log('handleAssign called', feedback.id);
    setModalState({ feedback, mode: 'assign' });
    // Set to the assigned user ID if exists, otherwise leave empty (will show placeholder)
    setAssignToUserId(feedback.assigned_to_id || '');
  };

  const handleAssignSubmit = () => {
    if (!selectedFeedback) {
      toast.error('No feedback selected');
      return;
    }
    
    // Handle unassignment (when __none__ is selected)
    if (assignToUserId === '__none__') {
      // For unassignment, we can either:
      // 1. Use a special API endpoint, or
      // 2. Set assigned_to_id to null via update endpoint
      // Let's use the update endpoint to set assigned_to_id to null
      updateFeedbackMutation.mutate({
        id: selectedFeedback.id,
        data: { assigned_to_id: null },
      });
      setModalState({ feedback: null, mode: null });
      setAssignToUserId('');
      return;
    }
    
    if (!assignToUserId) {
      toast.error('Please select a team member to assign');
      return;
    }
    
    assignFeedbackMutation.mutate({
      feedbackId: selectedFeedback.id,
      assignedToId: assignToUserId,
    });
  };

  const closeModal = () => {
    console.log('closeModal called');
    setModalState({ feedback: null, mode: null });
    setAssignToUserId('');
    setNewComment('');
    setIsInternalComment(false);
    setResolutionNotes('');
  };
  
  const createFeedbackMutation = useMutation({
    mutationFn: (data: any) => api.feedback.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      toast.success('Feedback created successfully');
      setIsNewFeedbackDialogOpen(false);
      setNewFeedbackForm({
        title: '',
        description: '',
        rating: '',
        feedback_text: '',
        severity: 'MEDIUM',
        source: 'MANUAL',
        sla_target_hours: '24',
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create feedback');
      console.error(error);
    },
  });

  // Handlers
  const handleStatusChange = (feedbackId: string, newStatus: string) => {
    updateFeedbackMutation.mutate({
      id: feedbackId,
      data: { status: newStatus },
    });
  };

  const handleSeverityChange = (feedbackId: string, newSeverity: string) => {
    updateFeedbackMutation.mutate({
      id: feedbackId,
      data: { severity: newSeverity },
    });
  };

  const handleResolveFeedback = () => {
    if (!selectedFeedback || !resolutionNotes.trim()) return;
    
    resolveFeedbackMutation.mutate({
      id: selectedFeedback.id,
      notes: resolutionNotes,
    });
  };

  const handleAddComment = () => {
    if (!selectedFeedback || !newComment.trim()) return;
    
    addCommentMutation.mutate({
      feedbackId: selectedFeedback.id,
      comment: newComment,
      isInternal: isInternalComment,
    });
  };
  
  const handleCreateFeedback = () => {
    if (!newFeedbackForm.title.trim() || !newFeedbackForm.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    
    // Get tenant_id from localStorage
    const tenantId = localStorage.getItem('tenant_id');
    if (!tenantId) {
      toast.error('Tenant ID not found');
      return;
    }
    
    const feedbackData: any = {
      tenant_id: tenantId,
      title: newFeedbackForm.title,
      description: newFeedbackForm.description,
      severity: newFeedbackForm.severity,
      source: newFeedbackForm.source,
      sla_target_hours: parseInt(newFeedbackForm.sla_target_hours),
    };
    
    // Add optional fields if they have values
    if (newFeedbackForm.rating) {
      feedbackData.rating = parseInt(newFeedbackForm.rating);
    }
    
    if (newFeedbackForm.feedback_text.trim()) {
      feedbackData.feedback_text = newFeedbackForm.feedback_text;
    }
    
    createFeedbackMutation.mutate(feedbackData);
  };

  // Handler for clicking stat cards to filter
  const handleCardClick = (filterType: 'status' | 'sla_status', value: string) => {
    // If clicking the same filter, clear it (toggle behavior)
    if (filterType === 'status' && filters.status === value) {
      setFilters({ ...filters, status: '' });
    } else if (filterType === 'sla_status' && filters.sla_status === value) {
      setFilters({ ...filters, sla_status: '' });
    } else {
      // Set the filter and clear the other filter type if needed
      if (filterType === 'status') {
        setFilters({ ...filters, status: value, sla_status: '' });
      } else {
        setFilters({ ...filters, sla_status: value, status: '' });
      }
      
      // Scroll to feedback table after a short delay to allow state update
      setTimeout(() => {
        feedbackTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ status: '', severity: '', sla_status: '', search: '' });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.status || filters.severity || filters.sla_status || filters.search;


  const slaHelp = getHelpContent('slaSystem');
  const workflowHelp = getHelpContent('feedbackWorkflow');
  const filteringHelp = getHelpContent('feedbackFiltering');

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Feedback Inbox</h1>
              {slaHelp && (
                <HelpIcon
                  tooltip="Learn about the SLA system"
                  modalTitle={slaHelp.title}
                  modalContent={slaHelp.content}
                  size="md"
                />
              )}
            </div>
            <p className="text-muted-foreground">
              Manage customer feedback and track SLA compliance
            </p>
          </div>
          <Button onClick={() => setIsNewFeedbackDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Feedback
          </Button>
        </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="inbox" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Inbox Tab */}
        <TabsContent value="inbox" className="space-y-6 mt-6">
          {/* Stats Date Range Selector */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Stats Date Range</CardTitle>
                <Select
                  value={statsDateRange}
                  onValueChange={setStatsDateRange}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
          </Card>

          {/* Stats Cards */}
      {!isLoadingStats && stats && (
        <div className="space-y-4">
          {/* All Time Stats */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-muted-foreground">All Time Stats</h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-xs h-7"
                >
                  Clear Filters
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                  filters.status === 'NEW' ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/50' : ''
                }`}
                onClick={() => handleCardClick('status', 'NEW')}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">New (All Time)</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total_new}</div>
                  <p className="text-xs text-muted-foreground mt-1">Status: NEW</p>
                </CardContent>
              </Card>
              
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                  filters.status === 'ASSIGNED' ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50/50' : ''
                }`}
                onClick={() => handleCardClick('status', 'ASSIGNED')}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Assigned (All Time)</CardTitle>
                  <User className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total_assigned}</div>
                  <p className="text-xs text-muted-foreground mt-1">Status: ASSIGNED</p>
                </CardContent>
              </Card>
              
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                  filters.status === 'IN_PROGRESS' ? 'ring-2 ring-yellow-500 border-yellow-500 bg-yellow-50/50' : ''
                }`}
                onClick={() => handleCardClick('status', 'IN_PROGRESS')}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">In Progress (All Time)</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total_in_progress}</div>
                  <p className="text-xs text-muted-foreground mt-1">Status: IN_PROGRESS</p>
                </CardContent>
              </Card>
              
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                  filters.status === 'RESOLVED' ? 'ring-2 ring-green-500 border-green-500 bg-green-50/50' : ''
                }`}
                onClick={() => handleCardClick('status', 'RESOLVED')}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Resolved (All Time)</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total_resolved}</div>
                  <p className="text-xs text-muted-foreground mt-1">Status: RESOLVED</p>
                </CardContent>
              </Card>
              
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                  filters.sla_status === 'BREACHED' ? 'ring-2 ring-red-500 border-red-500 bg-red-50/50' : ''
                }`}
                onClick={() => handleCardClick('sla_status', 'BREACHED')}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Overdue (All Time)</CardTitle>
                  <XCircle className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{stats.total_overdue}</div>
                  <p className="text-xs text-muted-foreground mt-1">SLA: BREACHED</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Date Range Stats */}
          {statsDateRange !== 'all' && (
            isLoadingDateRangeStats ? (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  Last {statsDateRange} Days Stats
                </h3>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              </div>
            ) : dateRangeStats && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Last {statsDateRange} Days Stats
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <Card
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] border-2 ${
                    filters.status === 'NEW' ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/50' : 'border-blue-200'
                  }`}
                  onClick={() => handleCardClick('status', 'NEW')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">New ({statsDateRange}d)</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dateRangeStats.total_new}</div>
                    <p className="text-xs text-muted-foreground mt-1">Status: NEW</p>
                  </CardContent>
                </Card>
                
                <Card
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                    filters.status === 'ASSIGNED' ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50/50' : ''
                  }`}
                  onClick={() => handleCardClick('status', 'ASSIGNED')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Assigned ({statsDateRange}d)</CardTitle>
                    <User className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dateRangeStats.total_assigned}</div>
                    <p className="text-xs text-muted-foreground mt-1">Status: ASSIGNED</p>
                  </CardContent>
                </Card>
                
                <Card
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                    filters.status === 'IN_PROGRESS' ? 'ring-2 ring-yellow-500 border-yellow-500 bg-yellow-50/50' : ''
                  }`}
                  onClick={() => handleCardClick('status', 'IN_PROGRESS')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">In Progress ({statsDateRange}d)</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dateRangeStats.total_in_progress}</div>
                    <p className="text-xs text-muted-foreground mt-1">Status: IN_PROGRESS</p>
                  </CardContent>
                </Card>
                
                <Card
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                    filters.status === 'RESOLVED' ? 'ring-2 ring-green-500 border-green-500 bg-green-50/50' : ''
                  }`}
                  onClick={() => handleCardClick('status', 'RESOLVED')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Resolved ({statsDateRange}d)</CardTitle>
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dateRangeStats.total_resolved}</div>
                    <p className="text-xs text-muted-foreground mt-1">Status: RESOLVED</p>
                  </CardContent>
                </Card>
                
                <Card
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                    filters.sla_status === 'BREACHED' ? 'ring-2 ring-red-500 border-red-500 bg-red-50/50' : ''
                  }`}
                  onClick={() => handleCardClick('sla_status', 'BREACHED')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Overdue ({statsDateRange}d)</CardTitle>
                    <XCircle className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">{dateRangeStats.total_overdue}</div>
                    <p className="text-xs text-muted-foreground mt-1">SLA: BREACHED</p>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] border-2 ${
                    !hasActiveFilters ? 'border-green-200' : 'border-green-200'
                  }`}
                  onClick={handleClearFilters}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total ({statsDateRange}d)</CardTitle>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dateRangeStats.total_feedback}</div>
                    <p className="text-xs text-muted-foreground mt-1">All Statuses</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            )
          )}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
            {filteringHelp && (
              <HelpIcon
                tooltip="Learn about filtering and search"
                modalTitle={filteringHelp.title}
                modalContent={filteringHelp.content}
                size="sm"
                variant="subtle"
              />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search feedback..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.status || "all"}
                onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="NEW">New</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Severity</label>
              <Select
                value={filters.severity || "all"}
                onValueChange={(value) => setFilters({ ...filters, severity: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All severities" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All severities</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">SLA Status</label>
              <Select
                value={filters.sla_status || "all"}
                onValueChange={(value) => setFilters({ ...filters, sla_status: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All SLA statuses" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All SLA statuses</SelectItem>
                  <SelectItem value="ON_TIME">On Time</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="BREACHED">Breached</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback Table */}
      <div ref={feedbackTableRef}>
        <Card>
          <CardHeader>
            <CardTitle>Feedback List</CardTitle>
          </CardHeader>
          <CardContent>
          {isLoadingFeedback ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>SLA</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbackList.map((feedback: Feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-semibold">{feedback.title}</div>
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {feedback.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSeverityColor(feedback.severity)}>
                          {feedback.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(feedback.status)}>
                          {feedback.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getSLAStatusIcon(feedback.sla_status)}
                          <Badge className={getSLAStatusColor(feedback.sla_status)}>
                            {formatSLAStatus(feedback.sla_status)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {feedback.assigned_to ? (
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {(feedback.assigned_to.first_name?.[0] || '').toUpperCase()}
                                {(feedback.assigned_to.last_name?.[0] || '').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">
                              {feedback.assigned_to.first_name || ''} {feedback.assigned_to.last_name || ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(feedback.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem
                              onClick={() => handleViewDetails(feedback)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleEdit(feedback)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleAssign(feedback)}
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              Assign
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {feedbackList.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No feedback found matching your criteria.
                </div>
              )}
              
              {/* Pagination Controls */}
              {feedbackList.length > 0 && (
                <div className="flex items-center justify-between px-2 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, (currentPage * pageSize) + feedbackList.length)} of {hasMore ? 'many' : (currentPage * pageSize) + feedbackList.length} feedback items
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0 || isLoadingFeedback}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground px-2">
                        Page {currentPage + 1}
                        {hasMore && ' +'}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!hasMore || isLoadingFeedback}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* View Details Modal (Read-only) */}
      {selectedFeedback && modalMode === 'view' && (
        <Dialog open={true} onOpenChange={closeModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedFeedback.title}
                <Badge className={getSeverityColor(selectedFeedback.severity)}>
                  {selectedFeedback.severity}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                View feedback details (read-only)
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Feedback Details - Read-only */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">
                    <Badge className={getStatusColor(selectedFeedback.status)}>
                      {selectedFeedback.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Severity</label>
                  <div className="mt-1">
                    <Badge className={getSeverityColor(selectedFeedback.severity)}>
                      {selectedFeedback.severity}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <div className="mt-1 p-3 bg-muted rounded-md">
                  {selectedFeedback.description}
                </div>
              </div>

              {/* Additional Feedback */}
              {selectedFeedback.feedback_text && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Additional Feedback</label>
                  <div className="mt-1 p-3 bg-muted rounded-md">
                    {selectedFeedback.feedback_text}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              {selectedFeedback.contact && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Contact</label>
                  <div className="mt-1 p-3 bg-muted rounded-md">
                    <div className="font-medium">
                      {selectedFeedback.contact.first_name || ''} {selectedFeedback.contact.last_name || ''}
                    </div>
                    {selectedFeedback.contact.email && (
                      <div className="text-sm text-muted-foreground">
                        {selectedFeedback.contact.email}
                      </div>
                    )}
                    {selectedFeedback.contact.company && (
                      <div className="text-sm text-muted-foreground">
                        {selectedFeedback.contact.company}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned To */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Assigned To</label>
                <div className="mt-1 p-3 bg-muted rounded-md">
                  {selectedFeedback.assigned_to ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {(selectedFeedback.assigned_to.first_name?.[0] || '').toUpperCase()}
                          {(selectedFeedback.assigned_to.last_name?.[0] || '').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {selectedFeedback.assigned_to.first_name || ''} {selectedFeedback.assigned_to.last_name || ''}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </div>
              </div>

              {/* SLA Status */}
              <div className="flex items-center gap-2">
                {getSLAStatusIcon(selectedFeedback.sla_status)}
                <span className="text-sm font-medium">
                  SLA: {formatSLAStatus(selectedFeedback.sla_status)} 
                  ({selectedFeedback.sla_target_hours} hours)
                </span>
              </div>

              {/* Comments */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Comments</label>
                <div className="mt-2 space-y-3">
                  {selectedFeedback.comments?.map((comment) => (
                    <div key={comment.id} className="p-3 bg-muted rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {(comment.user?.first_name?.[0] || '').toUpperCase()}{(comment.user?.last_name?.[0] || '').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">
                            {comment.user?.first_name || ''} {comment.user?.last_name || ''}
                          </span>
                          {comment.is_internal && (
                            <Badge variant="outline" className="text-xs">
                              Internal
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                      <p className="text-sm">{comment.comment}</p>
                      {(comment.status_change || comment.severity_change) && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {comment.status_change && (
                            <span>Status changed to {comment.status_change}</span>
                          )}
                          {comment.severity_change && (
                            <span>Severity changed to {comment.severity_change}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {(!selectedFeedback.comments || selectedFeedback.comments.length === 0) && (
                    <div className="text-sm text-muted-foreground">No comments yet</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={closeModal}>
                Close
              </Button>
              <Button onClick={() => { setModalState(prev => ({ ...prev, mode: 'edit' })); }}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Modal */}
      {selectedFeedback && modalMode === 'edit' && (
        <Dialog open={true} onOpenChange={closeModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Edit Feedback: {selectedFeedback.title}
                <Badge className={getSeverityColor(selectedFeedback.severity)}>
                  {selectedFeedback.severity}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Feedback Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <Select
                    value={selectedFeedback.status}
                    onValueChange={(value) => handleStatusChange(selectedFeedback.id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="NEW">New</SelectItem>
                      <SelectItem value="ASSIGNED">Assigned</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="RESOLVED">Resolved</SelectItem>
                      <SelectItem value="CLOSED">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Severity</label>
                  <Select
                    value={selectedFeedback.severity}
                    onValueChange={(value) => handleSeverityChange(selectedFeedback.id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="CRITICAL">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <div className="mt-1 p-3 bg-muted rounded-md">
                  {selectedFeedback.description}
                </div>
              </div>

              {/* Additional Feedback */}
              {selectedFeedback.feedback_text && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Additional Feedback</label>
                  <div className="mt-1 p-3 bg-muted rounded-md">
                    {selectedFeedback.feedback_text}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              {selectedFeedback.contact && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Contact</label>
                  <div className="mt-1 p-3 bg-muted rounded-md">
                    <div className="font-medium">
                      {selectedFeedback.contact.first_name || ''} {selectedFeedback.contact.last_name || ''}
                    </div>
                    {selectedFeedback.contact.email && (
                      <div className="text-sm text-muted-foreground">
                        {selectedFeedback.contact.email}
                      </div>
                    )}
                    {selectedFeedback.contact.company && (
                      <div className="text-sm text-muted-foreground">
                        {selectedFeedback.contact.company}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SLA Status */}
              <div className="flex items-center gap-2">
                {getSLAStatusIcon(selectedFeedback.sla_status)}
                <span className="text-sm font-medium">
                  SLA: {formatSLAStatus(selectedFeedback.sla_status)} 
                  ({selectedFeedback.sla_target_hours} hours)
                </span>
              </div>

              {/* Comments */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Comments</label>
                <div className="mt-2 space-y-3">
                  {selectedFeedback.comments?.map((comment) => (
                    <div key={comment.id} className="p-3 bg-muted rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {(comment.user?.first_name?.[0] || '').toUpperCase()}{(comment.user?.last_name?.[0] || '').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">
                            {comment.user?.first_name || ''} {comment.user?.last_name || ''}
                          </span>
                          {comment.is_internal && (
                            <Badge variant="outline" className="text-xs">
                              Internal
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                      <p className="text-sm">{comment.comment}</p>
                      {(comment.status_change || comment.severity_change) && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {comment.status_change && (
                            <span>Status changed to {comment.status_change}</span>
                          )}
                          {comment.severity_change && (
                            <span>Severity changed to {comment.severity_change}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={3}
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                        />
                        <span className="text-sm">Internal comment</span>
                      </label>
                      <Button
                        size="sm"
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || addCommentMutation.isPending}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Add Comment
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resolution */}
              {selectedFeedback.status !== 'RESOLVED' && selectedFeedback.status !== 'CLOSED' && (
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-muted-foreground">Resolution Notes</label>
                  <Textarea
                    placeholder="Enter resolution notes..."
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    rows={3}
                    className="mt-2"
                  />
                  <Button
                    className="mt-2"
                    onClick={handleResolveFeedback}
                    disabled={!resolutionNotes.trim() || resolveFeedbackMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Resolve Feedback
                  </Button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Assign Modal */}
      {selectedFeedback && modalMode === 'assign' && (
        <Dialog open={true} onOpenChange={closeModal}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle>Assign Feedback</DialogTitle>
              <DialogDescription>
                Assign "{selectedFeedback.title}" to a team member
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign To</label>
                {isLoadingMembers ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <Select
                    value={assignToUserId || undefined}
                    onValueChange={(value) => setAssignToUserId(value || '__none__')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a team member..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__none__">Unassign</SelectItem>
                      {tenantMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.first_name} {member.last_name} {member.email && `(${member.email})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {tenantMembers.length === 0 && !isLoadingMembers && (
                  <p className="text-sm text-muted-foreground">
                    No team members found. Please add team members first.
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                onClick={handleAssignSubmit}
                disabled={assignFeedbackMutation.isPending || updateFeedbackMutation.isPending}
              >
                {assignFeedbackMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* New Feedback Dialog */}
      <Dialog open={isNewFeedbackDialogOpen} onOpenChange={setIsNewFeedbackDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Create New Feedback</DialogTitle>
            <DialogDescription>
              Create a new feedback entry to track customer feedback and manage SLA compliance.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Title *</label>
              <Input
                placeholder="Enter feedback title..."
                value={newFeedbackForm.title}
                onChange={(e) => setNewFeedbackForm({ ...newFeedbackForm, title: e.target.value })}
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Description *</label>
              <Textarea
                placeholder="Enter detailed description..."
                value={newFeedbackForm.description}
                onChange={(e) => setNewFeedbackForm({ ...newFeedbackForm, description: e.target.value })}
                rows={4}
              />
            </div>
            
            {/* Source and Severity Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Source *</label>
                <Select
                  value={newFeedbackForm.source}
                  onValueChange={(value) => setNewFeedbackForm({ ...newFeedbackForm, source: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="MANUAL">Manual</SelectItem>
                    <SelectItem value="RATING_GATE">Rating Gate</SelectItem>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="PHONE">Phone</SelectItem>
                    <SelectItem value="WEB_FORM">Web Form</SelectItem>
                    <SelectItem value="API">API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Severity *</label>
                <Select
                  value={newFeedbackForm.severity}
                  onValueChange={(value) => setNewFeedbackForm({ ...newFeedbackForm, severity: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="CRITICAL">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Rating and SLA Hours Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Rating (Optional)</label>
                <div className="flex gap-2">
                  <Select
                    value={newFeedbackForm.rating || undefined}
                    onValueChange={(value) => setNewFeedbackForm({ ...newFeedbackForm, rating: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select rating..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="1">1 - Poor</SelectItem>
                      <SelectItem value="2">2 - Fair</SelectItem>
                      <SelectItem value="3">3 - Good</SelectItem>
                      <SelectItem value="4">4 - Very Good</SelectItem>
                      <SelectItem value="5">5 - Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                  {newFeedbackForm.rating && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setNewFeedbackForm({ ...newFeedbackForm, rating: '' })}
                      className="px-2"
                      title="Clear rating"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">SLA Target (Hours) *</label>
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={newFeedbackForm.sla_target_hours}
                  onChange={(e) => setNewFeedbackForm({ ...newFeedbackForm, sla_target_hours: e.target.value })}
                />
              </div>
            </div>
            
            {/* Additional Feedback Text */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Additional Feedback (Optional)</label>
              <Textarea
                placeholder="Enter any additional feedback or comments..."
                value={newFeedbackForm.feedback_text}
                onChange={(e) => setNewFeedbackForm({ ...newFeedbackForm, feedback_text: e.target.value })}
                rows={3}
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsNewFeedbackDialogOpen(false)}
                disabled={createFeedbackMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateFeedback}
                disabled={createFeedbackMutation.isPending || !newFeedbackForm.title.trim() || !newFeedbackForm.description.trim()}
              >
                {createFeedbackMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Feedback
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6 mt-6">
          {/* Date Range Selector */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Feedback Analytics
                </CardTitle>
                <Select
                  value={analyticsDateRange}
                  onValueChange={setAnalyticsDateRange}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
          </Card>

          {isLoadingAnalytics ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : analytics ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics?.total_feedback ?? 0}</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Resolution Time</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {analytics?.avg_resolution_time_hours ? 
                        `${analytics.avg_resolution_time_hours.toFixed(1)}h` : 
                        'N/A'
                      }
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">SLA Breach Rate</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${(analytics?.sla_breach_rate ?? 0) > 10 ? 'text-red-600' : 'text-green-600'}`}>
                      {analytics?.sla_breach_rate?.toFixed(1) ?? '0.0'}%
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Team Members</CardTitle>
                    <User className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics?.assigned_to_performance?.length ?? 0}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Feedback Trends Chart */}
              {analytics?.feedback_over_time && analytics.feedback_over_time.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Feedback Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <Line
                        data={{
                          labels: analytics.feedback_over_time.map(d => format(new Date(d.date), 'MMM dd')),
                          datasets: [
                            {
                              label: 'Total Feedback',
                              data: analytics.feedback_over_time.map(d => d.count),
                              borderColor: 'rgb(59, 130, 246)',
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              tension: 0.4,
                            },
                            {
                              label: 'Resolved',
                              data: analytics.feedback_over_time.map(d => d.resolved),
                              borderColor: 'rgb(34, 197, 94)',
                              backgroundColor: 'rgba(34, 197, 94, 0.1)',
                              tension: 0.4,
                            },
                            {
                              label: 'SLA Breached',
                              data: analytics.feedback_over_time.map(d => d.sla_breached),
                              borderColor: 'rgb(239, 68, 68)',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              tension: 0.4,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top' as const,
                            },
                            title: {
                              display: false,
                            },
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                precision: 0,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Severity & Status Distribution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analytics?.feedback_by_severity && Object.keys(analytics.feedback_by_severity).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Feedback by Severity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <Bar
                          data={{
                            labels: Object.keys(analytics.feedback_by_severity),
                            datasets: [{
                              label: 'Count',
                              data: Object.values(analytics.feedback_by_severity),
                              backgroundColor: [
                                'rgba(34, 197, 94, 0.7)',
                                'rgba(251, 191, 36, 0.7)',
                                'rgba(249, 115, 22, 0.7)',
                                'rgba(239, 68, 68, 0.7)',
                              ],
                            }],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  precision: 0,
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {analytics?.feedback_by_status && Object.keys(analytics.feedback_by_status).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Feedback by Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <Bar
                          data={{
                            labels: Object.keys(analytics.feedback_by_status).map(s => s.replace('_', ' ')),
                            datasets: [{
                              label: 'Count',
                              data: Object.values(analytics.feedback_by_status),
                              backgroundColor: [
                                'rgba(59, 130, 246, 0.7)',
                                'rgba(168, 85, 247, 0.7)',
                                'rgba(251, 191, 36, 0.7)',
                                'rgba(34, 197, 94, 0.7)',
                                'rgba(156, 163, 175, 0.7)',
                              ],
                            }],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  precision: 0,
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Team Performance Table */}
              {analytics.assigned_to_performance && analytics.assigned_to_performance.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      Team Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Team Member</TableHead>
                          <TableHead className="text-right">Assigned</TableHead>
                          <TableHead className="text-right">Resolved</TableHead>
                          <TableHead className="text-right">In Progress</TableHead>
                          <TableHead className="text-right">Resolution Rate</TableHead>
                          <TableHead className="text-right">Avg Time</TableHead>
                          <TableHead className="text-right">SLA Breach Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.assigned_to_performance.map((member) => (
                          <TableRow key={member.user_id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="text-xs">
                                    {member.user_name.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                {member.user_name}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{member.total_assigned}</TableCell>
                            <TableCell className="text-right">
                              <span className="text-green-600 font-medium">{member.resolved}</span>
                            </TableCell>
                            <TableCell className="text-right">{member.in_progress}</TableCell>
                            <TableCell className="text-right">
                              <Badge className={member.resolution_rate >= 80 ? 'bg-green-100 text-green-800' : member.resolution_rate >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                                {member.resolution_rate}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {member.avg_resolution_time_hours ? 
                                `${member.avg_resolution_time_hours}h` : 
                                'N/A'
                              }
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge className={member.sla_breach_rate < 5 ? 'bg-green-100 text-green-800' : member.sla_breach_rate < 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                                {member.sla_breach_rate}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Top Keywords */}
              {analytics?.top_keywords && analytics.top_keywords.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Tag className="h-5 w-5" />
                      Top Keywords
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {analytics.top_keywords.map((keyword, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-sm px-3 py-1"
                        >
                          {keyword.keyword} ({keyword.count})
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* No data message */}
              {analytics && analytics.total_feedback === 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12 text-muted-foreground">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No feedback data available for the selected period.</p>
                      <p className="text-sm mt-2">Try selecting a different date range or create some feedback.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-muted-foreground">
                  <p>Failed to load analytics data.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      </div>
    </TooltipProvider>
  );
}
